import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useQuery } from "@tanstack/react-query";
import { ProjectMember } from "@/types/project";


// Get project members
async function getProjectMembers(
  projectId: string,
  authHeaders: Record<string, string>
): Promise<ProjectMember[]> {
  const url = `${config.isometricApiUrl}/projects/${projectId}/members`;

  // Use plain fetch instead of apiFetch to avoid auto-logout on 403
  // (admin viewing a project they don't own will get 403)
  const response = await fetch(url, {
    method: "GET",
    headers: authHeaders,
  });

  if (response.status === 403) {
    return [];
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

// Hook to get project members
export function useGetProjectMembersService(projectId: string, enabled: boolean = true) {
  const { getAuthHeaders, isAuthenticated } = useAuthHeaders();

  return useQuery({
    queryKey: ["project-members", projectId],
    queryFn: async () => {
      return await getProjectMembers(projectId, getAuthHeaders());
    },
    enabled: !!enabled && isAuthenticated && !!projectId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
